import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Divider from 'components/divider/Divider';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCustomizations, updateCustomizations } from './invoiceSettingsSlice';
import {
  useLazyGetEstimateSummaryQuery,
  useLazyGetEstimateTncSuggestionsQuery,
  useUpdateTeamLevelEstimateTncMutation,
  useUpdateTeamLevelEstimateTncStatusMutation
} from 'containers/estimates/api';
import { selectCurrentTeam, selectCurrentTeamFeePermissions } from 'containers/app/appSlice';
import CreateEditTermsModal from './components/CreateEditTermsModal';
import TermsAndConditionsSection from './components/TermsAndConditionsSection';
import { Loader } from 'components';
import { useLazyGetMerchantCustomizationsQuery } from './api';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from 'utils/firebase';
import { CustomizationsUpdateType } from './constants';
import { REGEX } from 'constants/common';
import { BackIcon } from 'assets/icons';

const ESTIMATE_TNC_TEXTS = {
  title: 'Estimate Terms & Conditions',
  switchLabel: 'Show Terms & Conditions in all Estimates',
  emptyStateDescription: `You can add a default text for Terms & Conditions, Return Policy, Support Policy, 
      Cancellation Policy, or any Custom Text with the estimate.`,
  addButtonText: 'Add Additional Text'
};

const EstimateSettings = () => {
  const [isEstimateTermsEdit, setIsEstimateTermsEdit] = useState(false);
  const [isEstimateTermsModalOpen, setIsEstimateTermsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastUpdatedCustomizationsAt, setLastUpdatedCustomizationsAt] = useState<number>(0);
  const navigate = useNavigate();

  const currentTeam = useAppSelector(selectCurrentTeam);
  const customizations = useAppSelector(selectCustomizations);
  const dispatch = useAppDispatch();
  const feeSettingsPermissions = useAppSelector(selectCurrentTeamFeePermissions);
  const { viewTnc, editTnc } = feeSettingsPermissions;

  const [getEstimateTermsSuggestionsQuery, getEstimateTermsSuggestionsState] = useLazyGetEstimateTncSuggestionsQuery();
  const [getEstimateSummaryQuery, getEstimateSummaryState] = useLazyGetEstimateSummaryQuery();
  const [updateEstimateTNCMutation] = useUpdateTeamLevelEstimateTncMutation();
  const [updateEstimateTNCStatusMutation] = useUpdateTeamLevelEstimateTncStatusMutation();
  const [
    getMerchantCustomizationsQuery,
    {
      isFetching: isGetMerchantCustomizationsFetching,
      isSuccess: isGetMerchantCustomizationsSuccess,
      data: getMerchantCustomizationsResponse
    }
  ] = useLazyGetMerchantCustomizationsQuery();

  const {
    data: getEstimateTermsSuggestionsResponse,
    isSuccess: getEstimateTermsSuggestionsSuccess,
    isFetching: isEstimateTermsSuggestionsFetching
  } = getEstimateTermsSuggestionsState;

  const {
    data: getEstimateSummaryResponse,
    isSuccess: getEstimateSummarySuccess,
    isFetching: isGetEstimateSummaryFetching
  } = getEstimateSummaryState;

  const fetchMerchantCustomizations = () => {
    getMerchantCustomizationsQuery({ teamId: currentTeam.id.toString() });
  };

  useEffect(() => {
    if (isGetMerchantCustomizationsSuccess && !isGetMerchantCustomizationsFetching) {
      const serviceFeePercentageWithNoTrailingZeros = getMerchantCustomizationsResponse.surcharge?.percentage.replace(
        REGEX.replceTrailingZeros,
        '$1$2$3'
      );
      dispatch(
        updateCustomizations({
          ...getMerchantCustomizationsResponse,
          surcharge: {
            ...getMerchantCustomizationsResponse.surcharge,
            percentage: serviceFeePercentageWithNoTrailingZeros,
            serviceFeeConfig: getMerchantCustomizationsResponse.serviceFeeConfig
          }
        })
      );

      setLoading(false);
    }
  }, [isGetMerchantCustomizationsSuccess, isGetMerchantCustomizationsFetching]);

  useEffect(() => {
    let unsubscribe = () => null;
    if (currentTeam?.id) {
      unsubscribe = onSnapshot(doc(db, 'Teams', currentTeam.id.toString()), document => {
        const lastUpdatedAtFromDoc = document.data()?.['CustomizationLastUpdatedAt'];
        if (lastUpdatedAtFromDoc > lastUpdatedCustomizationsAt) {
          if (document.data()?.['CustomizationUpdatedWeb'] === CustomizationsUpdateType.ESTIMATE_TNC)
            fetchMerchantCustomizations();
          setLastUpdatedCustomizationsAt(lastUpdatedAtFromDoc);
        }
      });
    }

    return () => {
      unsubscribe();
    };
  }, [currentTeam.id]);

  useEffect(() => {
    if (currentTeam.id) {
      // setLoading(true);
      if (editTnc) {
        getEstimateTermsSuggestionsQuery({ teamId: currentTeam.id });
      }
      getEstimateSummaryQuery({ teamId: currentTeam.id });
    }
  }, [currentTeam.id]);

  useEffect(() => {
    if (getEstimateTermsSuggestionsSuccess && getEstimateSummarySuccess) {
      setLoading(false);
    }
  }, [getEstimateTermsSuggestionsSuccess, getEstimateSummarySuccess]);

  const resetEstimateTermsModalState = () => {
    setIsEstimateTermsModalOpen(false);
    setIsEstimateTermsEdit(false);
  };

  const handlAddEstimateTermsClick = () => {
    setIsEstimateTermsModalOpen(true);
    setIsEstimateTermsEdit(false);
  };

  const handlEditEstimateTermsClick = () => {
    setIsEstimateTermsModalOpen(true);
    setIsEstimateTermsEdit(true);
  };

  const handleSaveEstimateTermsClick = async (terms: string, applyPendingEstimates: boolean) => {
    setLoading(true);
    resetEstimateTermsModalState();
    await updateEstimateTNCMutation({
      teamId: currentTeam.id,
      body: {
        tnc: terms,
        applyPendingEstimates
      }
    });
    fetchMerchantCustomizations();
    setLoading(false);
  };

  const onCancelEstimateTermsClick = () => {
    resetEstimateTermsModalState();
  };

  const handlDeleteEstimateTermsClick = async () => {
    setLoading(true);
    await updateEstimateTNCMutation({
      teamId: currentTeam.id,
      body: {
        tnc: '',
        applyPendingEstimates: false
      }
    });
    fetchMerchantCustomizations();
    setLoading(false);
  };

  const toggleEstimateTerms = () => {
    if (customizations.estimateTnc?.tnc) {
      setLoading(true);
      // const updatedSettings = {
      //   ...customizations,
      //   estimateTnc: {
      //     ...customizations.estimateTnc,
      //     enabled: !customizations.estimateTnc.enabled
      //   }
      // };
      // dispatch(updateCustomizations(updatedSettings));
      updateEstimateTNCStatusMutation({
        teamId: currentTeam.id,
        enabled: !customizations.estimateTnc.enabled
      });
      fetchMerchantCustomizations();
    } else {
      handlAddEstimateTermsClick();
    }
    setLoading(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const canOpenEstimateTermsModal =
    getEstimateTermsSuggestionsSuccess &&
    !isEstimateTermsSuggestionsFetching &&
    getEstimateSummarySuccess &&
    !isGetEstimateSummaryFetching;

  const noPermissions = !viewTnc && !editTnc;

  return noPermissions ? (
    <div className="flex h-full flex-col items-center justify-center gap-5">
      <div className="flex flex-col items-center gap-5">
        <div className="text-xl text-primaryText">You do not have permissions to view estimate settings</div>
      </div>
    </div>
  ) : (
    <>
      <div className="h-full w-full">
        <div className="px-[30px] pb-16 pt-6">
          <div className="flex w-full">
            <BackIcon
              className="path path-stroke-current mr-5 mt-2 h-5 w-5 cursor-pointer text-primaryText"
              onClick={handleBack}
            />
            <div>
              <div className="text-2xl font-bold text-primaryText">Estimate settings</div>
              <div className="mt-3 mb-7 text-17px font-normal text-primaryText">
                Configure the default settings for estimates created by everyone on the team.
              </div>
            </div>
          </div>
          <Divider className="w-full border-borderGray" />
          <div className="relative w-[560px] md:w-[490px] lg:w-[820px]">
            {viewTnc && (
              <>
                <div className="mt-8">
                  <TermsAndConditionsSection
                    editTnc={editTnc}
                    tnc={customizations.estimateTnc}
                    toggleTerms={toggleEstimateTerms}
                    handlEditTermsClick={handlEditEstimateTermsClick}
                    handlDeleteTermsClick={handlDeleteEstimateTermsClick}
                    handlAddTermsClick={handlAddEstimateTermsClick}
                    texts={ESTIMATE_TNC_TEXTS}
                  />
                </div>
              </>
            )}
            {loading && <Loader />}
          </div>
        </div>
      </div>
      {canOpenEstimateTermsModal && (
        <CreateEditTermsModal
          suggestions={getEstimateTermsSuggestionsResponse.data}
          open={isEstimateTermsModalOpen}
          tncText={customizations.estimateTnc?.tnc}
          isEdit={isEstimateTermsEdit}
          toggleOpen={onCancelEstimateTermsClick}
          onSave={handleSaveEstimateTermsClick}
          numberOfInvoices={getEstimateSummaryResponse.data.PENDING}
          updatePendingItemsText="Update pending estimates"
        />
      )}
    </>
  );
};

export default EstimateSettings;
